import React, { Fragment, useEffect, useRef } from "react";
import { Field } from "formik";
import { isValidArray } from "./common";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";

const AUSTRALIA_BOUNDS = { north: -10.0, south: -44.0, east: 154.0, west: 113.0 };

const PLACES_REGION_CODES = (process.env.REACT_APP_PLACES_REGION_CODES ?? "")
  .split(",")
  .map((code) => code.trim().toLowerCase())
  .filter(Boolean);

const ADDRESS_OPTIONS = {
  types: ["address"],
  bounds: AUSTRALIA_BOUNDS,
  strictBounds: false,
  fields: ["address_components", "geometry", "name"],
  ...(PLACES_REGION_CODES.length > 0 && { componentRestrictions: { country: PLACES_REGION_CODES } }),
};

export const postcodeAtLocation = (location) =>
  new Promise((resolve) => {
    if (!location || !window.google?.maps?.Geocoder) {
      resolve("");
      return;
    }
    new window.google.maps.Geocoder().geocode({ location }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve("");
        return;
      }
      for (const result of results) {
        const found = result.address_components?.find((c) => c.types.includes("postal_code"));
        if (found) {
          resolve(found.long_name);
          return;
        }
      }
      resolve("");
    });
  });

export const parseGoogleAddress = (place) => {
  const address = place?.addressComponents;
  if (!isValidArray(address)) return null;

  const componentOf = (...types) => address.find((addr) => types.some((type) => addr.types.includes(type)));
  const street_number = componentOf("street_number");
  const route = componentOf("route");
  const subpremise = componentOf("subpremise");
  const suburb = componentOf("locality", "sublocality", "postal_town", "administrative_area_level_2");
  const state = componentOf("administrative_area_level_1");
  const country = componentOf("country");
  const postCode = componentOf("postal_code");

  const address1 = [street_number?.longText, route?.longText].filter(Boolean).join(" ") || address[0].longText;

  if (!address1) return null;

  return {
    address1: address1,
    address2: subpremise?.longText ?? "",
    suburb: suburb?.longText ?? "",
    state: state?.shortText ?? "",
    postCode: postCode?.longText ?? "",
    country: country?.longText ?? "",
  };
};

const toPlace = (googlePlace) => ({
  addressComponents: googlePlace.address_components.map((component) => ({
    longText: component.long_name,
    shortText: component.short_name,
    types: component.types,
  })),
  location: googlePlace.geometry?.location ?? null,
});

const isSuggestionListOpen = () =>
  Array.from(document.querySelectorAll(".pac-container")).some(
    (el) => el.offsetParent !== null && el.offsetHeight > 0,
  );

const DEFAULT_FIELDS = { suburb: "suburb", state: "state", postCode: "postCode", country: "country" };

const BROWSER_AUTOFILL_OFF = "new-password";

const GoogleAddressField = ({
  formik,
  setFieldValue,
  formValue,
  name = "address1",
  label,
  errors,
  touched,
  innerRef,
  onChange,
  onKeyDown = () => {},
  onAddressSelected,
  required = false,
  tabIndex,
  maxLength,
  fields = DEFAULT_FIELDS,
}) => {
  const inputRef = useRef(null);
  const latestProps = useRef({});
  latestProps.current = { formik, setFieldValue, name, onAddressSelected, fields };

  const setInputRef = (element) => {
    inputRef.current = element;
    if (typeof innerRef === "function") innerRef(element);
    else if (innerRef) innerRef.current = element;
  };

  const applyPlace = async (googlePlace) => {
    if (!isValidArray(googlePlace?.address_components)) return;
    const { formik, setFieldValue, name, onAddressSelected, fields } = latestProps.current;

    const place = toPlace(googlePlace);
    const parsed = parseGoogleAddress(place);
    if (!parsed) return;
    if (!parsed.postCode && place.location) {
      parsed.postCode = await postcodeAtLocation(place.location);
    }

    setFieldValue(name, parsed.address1);
    const target = { ...DEFAULT_FIELDS, ...fields };
    setFieldValue(target.suburb, parsed.suburb);
    setFieldValue(target.state, parsed.state);
    setFieldValue(target.postCode, parsed.postCode);
    setFieldValue(target.country, parsed.country);

    formik?.setFieldTouched?.(name, true);
    formik?.setFieldError?.(name, undefined);

    if (onAddressSelected) onAddressSelected(parsed, setFieldValue, place);
  };

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;

    let autocomplete = null;
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled) return;
        autocomplete = new google.maps.places.Autocomplete(input, ADDRESS_OPTIONS);
        autocomplete.addListener("place_changed", () => applyPlace(autocomplete.getPlace()));
      })
      .catch(() => {});

    const blockEnterWhileSuggesting = (event) => {
      if (event.key === "Enter" && isSuggestionListOpen()) event.preventDefault();
    };
    input.addEventListener("keydown", blockEnterWhileSuggesting, true);

    const keepBrowserAutofillOff = () => {
      if (input.getAttribute("autocomplete") !== BROWSER_AUTOFILL_OFF) {
        input.setAttribute("autocomplete", BROWSER_AUTOFILL_OFF);
      }
    };
    const autofillObserver = new MutationObserver(keepBrowserAutofillOff);
    autofillObserver.observe(input, { attributes: true, attributeFilter: ["autocomplete"] });
    keepBrowserAutofillOff();

    return () => {
      cancelled = true;
      if (autocomplete) window.google?.maps?.event?.clearInstanceListeners(autocomplete);
      input.removeEventListener("keydown", blockEnterWhileSuggesting, true);
      autofillObserver.disconnect();
    };
  }, []);

  const handleInputChange = (e) => {
    if (onChange) onChange(e, setFieldValue);
    else setFieldValue(name, e.target.value);
  };

  return (
    <Fragment>
      <Field
        id={name}
        name={name}
        innerRef={setInputRef}
        className={`form-control form-input ${touched && errors ? "error-message" : ""}`}
        type="text"
        placeholder=""
        value={formValue[name] ?? ""}
        onChange={handleInputChange}
        onKeyDown={onKeyDown}
        autoComplete={BROWSER_AUTOFILL_OFF}
        required={required}
        tabIndex={tabIndex}
        maxLength={maxLength}
      />
      <label htmlFor={name}>
        {label}
        {required && <span className="gl-form-asterisk">*</span>}
      </label>
    </Fragment>
  );
};

export default GoogleAddressField;
